import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Truck, User, Mail, Phone, MapPin } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { cn } from '../../components/ui/utils';

interface AddSupplierModalProps {
    isOpen: boolean;
    onClose: () => void;
    onRefresh: () => void;
    supplier?: any;
}

const AddSupplierModal: React.FC<AddSupplierModalProps> = ({ isOpen, onClose, onRefresh, supplier }) => {
    const [formData, setFormData] = useState({
        name: '',
        contactName: '',
        email: '',
        phone: '',
        address: '',
        isActive: true
    });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (supplier) {
            setFormData({
                name: supplier.name || '',
                contactName: supplier.contactName || '',
                email: supplier.email || '',
                phone: supplier.phone || '',
                address: supplier.address || '',
                isActive: supplier.isActive !== undefined ? supplier.isActive : true
            });
        } else {
            setFormData({
                name: '',
                contactName: '',
                email: '',
                phone: '',
                address: '',
                isActive: true
            });
        }
    }, [supplier, isOpen]);

    const handleSubmit = async () => {
        if (!formData.name) {
            toast.error('Supplier name is required');
            return;
        }

        try {
            setLoading(true);
            if (supplier) {
                await api.patch(`/api/inventory/suppliers/${supplier.id}`, formData);
                toast.success('Supplier updated successfully');
            } else {
                await api.post('/api/inventory/suppliers', formData);
                toast.success('Supplier created successfully');
            }
            onRefresh();
            onClose();
        } catch (err: any) {
            toast.error(err.message || 'Failed to save supplier');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-lg rounded-[28px] sm:rounded-[32px] p-0 overflow-hidden border-none shadow-2xl flex flex-col">
                <div className="p-5 sm:p-6 border-b border-gray-50 flex-shrink-0">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-3 text-lg sm:text-2xl font-black tracking-tight">
                            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-blue-50 flex items-center justify-center shadow-sm">
                                <Truck size={20} className="text-blue-600" />
                            </div>
                            {supplier ? 'Edit Provider' : 'New Supplier'}
                        </DialogTitle>
                    </DialogHeader>
                </div>

                <div className="p-5 sm:p-8 space-y-5 sm:space-y-6 flex-1 overflow-y-auto custom-scrollbar">
                    <div className="space-y-5 sm:space-y-6">
                        <div className="space-y-1.5 sm:space-y-2">
                            <label className="text-[9px] sm:text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">Entity Name *</label>
                            <div className="relative group">
                                <Truck size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-blue-500 transition-colors" />
                                <Input
                                    placeholder="e.g., Global Industrial Supplies"
                                    className="pl-12 rounded-2xl border-gray-100 bg-gray-50/50 h-12 font-medium focus:bg-white transition-all shadow-inner"
                                    value={formData.name}
                                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                            <div className="space-y-1.5 sm:space-y-2">
                                <label className="text-[9px] sm:text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">Key Contact</label>
                                <div className="relative group">
                                    <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-blue-500 transition-colors" />
                                    <Input
                                        placeholder="e.g., John Doe"
                                        className="pl-12 rounded-2xl border-gray-100 bg-gray-50/50 h-12 font-medium focus:bg-white transition-all shadow-inner"
                                        value={formData.contactName}
                                        onChange={e => setFormData({ ...formData, contactName: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="space-y-1.5 sm:space-y-2">
                                <label className="text-[9px] sm:text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">Phone Line</label>
                                <div className="relative group">
                                    <Phone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-blue-500 transition-colors" />
                                    <Input
                                        placeholder="+1 --- --- ----"
                                        className="pl-12 rounded-2xl border-gray-100 bg-gray-50/50 h-12 font-medium focus:bg-white transition-all shadow-inner"
                                        value={formData.phone}
                                        onChange={e => setFormData({ ...formData, phone: e.target.value })}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="space-y-1.5 sm:space-y-2">
                            <label className="text-[9px] sm:text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">Official Email</label>
                            <div className="relative group">
                                <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-blue-500 transition-colors" />
                                <Input
                                    placeholder="e.g., supplier@idarah.com"
                                    className="pl-12 rounded-2xl border-gray-100 bg-gray-50/50 h-12 font-medium focus:bg-white transition-all shadow-inner"
                                    value={formData.email}
                                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="space-y-1.5 sm:space-y-2">
                            <label className="text-[9px] sm:text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">Address</label>
                            <div className="relative group">
                                <MapPin size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-blue-500 transition-colors" />
                                <Input
                                    placeholder="Business HQ location..."
                                    className="pl-12 rounded-2xl border-gray-100 bg-gray-50/50 h-12 font-medium focus:bg-white transition-all shadow-inner"
                                    value={formData.address}
                                    onChange={e => setFormData({ ...formData, address: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="flex items-center justify-between p-3 sm:p-4 bg-gray-50/50 border border-transparent hover:border-blue-100 rounded-2xl group transition-all gap-3">
                            <div className="min-w-0">
                                <p className="text-xs sm:text-sm font-black text-gray-900 tracking-tight">Verified & Active</p>
                                <p className="text-[9px] sm:text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-0.5 leading-relaxed">Allow in procurement</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setFormData({ ...formData, isActive: !formData.isActive })}
                                className={cn(
                                    "w-11 h-6 sm:w-12 sm:h-6 rounded-full relative transition-all duration-300 shadow-inner shrink-0",
                                    formData.isActive ? "bg-blue-500" : "bg-gray-200"
                                )}
                            >
                                <div className={cn(
                                    "absolute top-1 w-4 h-4 bg-white rounded-full transition-all duration-300 shadow-xl",
                                    formData.isActive ? "left-6 sm:left-7" : "left-1"
                                )} />
                            </button>
                        </div>
                    </div>
                </div>

                <div className="p-5 sm:p-6 bg-white border-t border-gray-50 flex flex-col sm:flex-row gap-2 sm:gap-3 shrink-0">
                    <Button
                        variant="ghost"
                        className="order-2 sm:order-1 sm:flex-1 rounded-2xl h-12 sm:h-14 font-black text-[11px] uppercase tracking-widest text-gray-400 hover:text-gray-900 transition-all"
                        onClick={onClose}
                    >
                        Discard
                    </Button>
                    <Button
                        className="order-1 sm:order-2 sm:flex-[2] rounded-2xl h-12 sm:h-14 bg-slate-900 hover:bg-slate-800 text-white font-black text-[11px] uppercase tracking-widest flex items-center justify-center gap-2 shadow-2xl shadow-slate-900/20 active:scale-[0.98] transition-all"
                        onClick={handleSubmit}
                        disabled={loading}
                    >
                        {loading ? (
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                            supplier ? 'Save Identity' : 'Register Provider'
                        )}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default AddSupplierModal;
